import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  PreferredRelationsShipsCreateSchema,
  PreferredRelationsShipsQuerySchema,
  PreferredRelationsShipsQuerySchemaAllSchema,
  PreferredRelationsShipsUpdateSchema,
} from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PREFERRED_RELATIONSHIPS'] });
};

export const useGetPreferredRelationshipsMutation: UseControlledMutation<PreferredRelationsShipsQuerySchema> = (
  params = {}
) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['PREFERRED_RELATIONSHIPS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<PreferredRelationsShipsQuerySchemaAllSchema>({
            endpoint: '/api/v1/preferred-relationships',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetPreferredRelationships(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['PREFERRED_RELATIONSHIPS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<PreferredRelationsShipsQuerySchemaAllSchema>({
        endpoint: '/api/v1/preferred-relationships',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: PreferredRelationsShipsQuerySchema }, ApiError, PreferredRelationsShipsCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/preferred-relationships/',
        payload,
      }),
  });

export function useGetPreferredRelationship(id: string) {
  return useSuspenseQuery({
    queryKey: ['PREFERRED_RELATIONSHIP', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PreferredRelationsShipsQuerySchema }>({
        endpoint: `/api/v1/preferred-relationships/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: PreferredRelationsShipsQuerySchema }, unknown, Partial<PreferredRelationsShipsUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/preferred-relationships/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PREFERRED_RELATIONSHIP_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/preferred-relationships/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
